import { ExternalLink, ArrowUpRight } from 'lucide-react';
import ecommerceImg from '../assets/ecommerce.png';
import healthTechImg from '../assets/health-tech.png';
import saasImg from '../assets/saas-analytics.png';

const projects = [
  {
    title: 'E-Commerce Platform',
    category: 'Web Development',
    description: 'A full-featured e-commerce platform with real-time inventory, payment processing, and analytics dashboard.',
    image: ecommerceImg,
    tags: ['React', 'Node.js', 'MongoDB', 'Stripe'],
  },
  {
    title: 'HealthTech Mobile App',
    category: 'Mobile Development',
    description: 'A health monitoring app with wearable integration, appointment scheduling, and telemedicine features.',
    image: healthTechImg,
    tags: ['React Native', 'Firebase', 'HealthKit'],
  },
  {
    title: 'SaaS Analytics Dashboard',
    category: 'Full Stack',
    description: 'Real-time analytics platform processing millions of events with custom visualization and reporting.',
    image: saasImg,
    tags: ['Next.js', 'PostgreSQL', 'Redis', 'D3.js'],
  },
];

const Projects = () => {
  return (
    <section 
      id="projects" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="projects-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/30 to-slate-950" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full border border-slate-800 bg-slate-900/50 text-sm text-blue-400 mb-4">
              Our Work
            </span>
            <h2 
              className="font-space text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              data-testid="projects-title"
            >
              Featured Projects
            </h2>
          </div>
          <p className="text-slate-400 max-w-md text-base md:text-lg md:text-right">
            A selection of our recent work showcasing our expertise
          </p>
        </div>

        {/* Projects Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="projects-grid"
        >
          {projects.map((project, index) => (
            <div
              key={index}
              className="group relative rounded-2xl overflow-hidden bg-slate-900/40 border border-slate-800 hover:border-blue-500/30 transition-all duration-500"
              data-testid={`project-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-slate-900/20 transition-colors duration-500" />
                
                {/* Category badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 rounded-full bg-slate-900/80 backdrop-blur-sm border border-slate-700 text-xs text-slate-300">
                    {project.category}
                  </span>
                </div>

                {/* Hover icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-space text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {project.description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-2.5 py-1 rounded-md bg-slate-800/50 text-xs text-slate-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <button 
            className="inline-flex items-center gap-2 px-6 py-3 border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-white rounded-full font-medium transition-all duration-300 hover:bg-blue-500/10"
            data-testid="projects-view-all-btn"
          >
            View All Projects
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
